import { I18N_DOMAIN } from '../../../editor/settings';
import {
	copyURLToClipboard,
	quizAlert,
	radioGroupArrowKeyNavigation,
	triggerNextQuizPopup,
	resetNextQuizPopup,
} from '../helpers';
import { validationCheck } from '../validation-check';

const { _nx, __ } = wp.i18n;
const { addQueryArgs } = wp.url;

/**
 * Jumps to the factoid of the last answered question.
 *
 * @param {HTMLElement} relevantQuestion - The question that has been answered
 *
 * @returns {void}
 *
 */
const jumpToFactoid = (relevantQuestion) => {
	const factoid = relevantQuestion.lastElementChild;
	const nextQuestion = relevantQuestion.nextElementSibling;

	if (
		factoid.lastElementChild.classList.contains('mavisPersonality-factoid') &&
		factoid.lastElementChild.innerHTML
	) {
		setTimeout(() => {
			factoid.scrollIntoView({ behavior: 'smooth' });
		}, 1000);
	} else {
		if (nextQuestion.classList.contains('mavisPersonality-submit-area')) {
			const lastAnswer = relevantQuestion.lastElementChild.querySelectorAll(
				'.wp-block-mavis-personality-answer',
			);
			setTimeout(() => {
				lastAnswer[lastAnswer.length - 1].scrollIntoView({ behavior: 'smooth' });
			}, 1000);
			return;
		}
		setTimeout(() => {
			nextQuestion.scrollIntoView({ behavior: 'smooth' });
		}, 1000);
	}
};

/**
 * Posts to the personality-answers custom endpoint.
 *
 * @param {array} selectedAnswers - Array of selected answer IDs.
 * @param {array} questionIds - Array of question IDs relevant to the answer IDs
 * @param {string} personalityId - The personality quiz unique id.
 *
 */
const sendSubmit = async (selectedAnswers, personalityId, questionIds) =>
	wp
		.apiFetch({
			path: `/mavis/v1/personality-results`,
			method: 'POST',
			data: {
				selected_answers: selectedAnswers,
				personality_id: personalityId,
				question_ids: questionIds,
			},
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Returns the question factoid.
 * @param {string} personalityId Personality post ID.
 * @param {string} questionId Question ID
 * @returns {object}
 */
const getQuestionFactoid = async (personalityId, questionId) =>
	wp
		.apiFetch({
			path: addQueryArgs('/mavis/v1/personality-question-factoid', {
				personality_id: personalityId,
				question_id: questionId,
			}),
		})
		.catch((err) => console.error(err.message)) // eslint-disable-line
		.then((data) => JSON.parse(data));

/**
 * Checks all questions are answered.
 *
 * @param {array} personality - Array of personality quizzes on a page/post
 *
 */
const allQuestionsAnswered = (personality, clickedQuestionId) => {
	let allAnswered = true;
	const personalityQuestions = personality.querySelectorAll('.wp-block-mavis-personality-question');

	personalityQuestions.forEach((personalityQuestion) => {
		const { questionAnswered, questionId } = personalityQuestion.dataset;

		if (questionId !== clickedQuestionId && questionAnswered === '0') {
			allAnswered = false;
		}
	});

	return allAnswered;
};

/**
 * Resets the quiz so the user can play again.
 * @returns {void}
 */
const handlePlayAgain = () => {
	// Get elements we need to reset.
	const personalityForm = document.querySelector('form.wp-block-mavis-personality');

	resetNextQuizPopup(personalityForm);

	const answerInputs = [...document.querySelectorAll('.wp-block-mavis-personality-answer input')];
	const selectedAnswerElements = [
		...document.querySelectorAll('.wp-block-mavis-personality-answer.is-selected'),
	];
	const results = document.querySelector('.mavisPersonality-results');
	const submitArea = document.querySelector('.mavisPersonality-submit-area');
	const postTitle = document.querySelector('.wp-block-post-title');

	const factoids = document.querySelectorAll('.mavisPersonality-factoid');
	const postAnswerContents = document.querySelectorAll('.mavisPersonality-postAnswerContent');
	const questions = document.querySelectorAll('.wp-block-mavis-personality-question');

	// Reset all inputs back to original state.
	answerInputs.forEach((input) => {
		input.checked = false; // eslint-disable-line no-param-reassign
		input.removeAttribute('disabled');
	});
	selectedAnswerElements.forEach((answer) => answer.classList.remove('is-selected'));
	submitArea.classList.remove('is-inactive');

	// Hide factoids.
	factoids.forEach((factoid) => {
		factoid.innerHTML = ''; // eslint-disable-line no-param-reassign
	});

	// Reset questions.
	postAnswerContents.forEach((content) => content.classList.add('is-hidden'));

	questions.forEach((question) => question.setAttribute('data-question-answered', '0'));

	// Hide results and remove the output we built to show the score.
	results.classList.remove('is-active');
	results.replaceChildren();

	// Reset the form.
	personalityForm.reset();

	if (window.dataLayer) {
		window.dataLayer.push({
			event: 'personalityQuizInteraction',
			interactionType: 'retake',
			quizName: document.querySelector('.wp-block-post-title').innerText,
		});
	}

	// Scroll back to top of post.
	postTitle.scrollIntoView();
};

/**
 * Checks if the amount of selected items, if all are selected remove class from button
 *
 * @param {array} selectedAnswers - Array of all the selected answers in the personality quiz
 * @param {array} questionIds - Array of question IDs relevant to the answer IDs
 * @param {string} personalityId - The unique personality quiz ID
 *
 */
const handleSubmit = async (selectedAnswers, personalityId, questionIds) => {
	const response = await sendSubmit(selectedAnswers, personalityId, questionIds);

	if (response) {
		const { personalityName, personalityDesc, personalityImage } = response;

		if (window.dataLayer) {
			window.dataLayer.push({
				event: 'personalityQuizComplete',
				quizName: document.querySelector('.wp-block-post-title').innerText,
				personalityOutcome: personalityName,
			});
		}

		const personality = document.querySelector(`[data-question-id="${personalityId}`);
		const personalityQuestions = personality.querySelectorAll(
			'.wp-block-mavis-personality-question',
		);

		personalityQuestions.forEach((element) => {
			if (element.classList.contains('is-unanswered')) {
				element.classList.remove('is-unanswered');
			}
		});

		const results = personality.querySelector('.mavisPersonality-results');

		// Update the aria-live attribute for when content is added
		results.setAttribute('aria-live', 'polite');

		const output = `
    <div class="mavisPersonality-resultsHeader">
      ${personalityImage}
      <span class="mavisPersonality-resultsHeader-container">
        <span class="mavisPersonality-resultsHeader-prefix">
          ${__('You are:', I18N_DOMAIN)}
        </span>
        <span class="mavisPersonality-resultsHeader-name">
          ${personalityName}
        </span>
      </span>
    </div>
    <div class="mavisPersonality-resultsContent">
      <span class="mavisPersonality-resultsDescription">${personalityDesc}</span>
      <span class="mavisPersonality_resultsContent-question">
        <strong>
          ${__('What do you want to do now?', 'mavis')}
        </strong>
      </span>
      <div class="mavisPersonality_resultsContent-buttons" role="button">
        <div class="wp-block-button">
          <button class="wp-block-button__link wp-element-button has-mavis-color-personality-secondary-background-color play-again">
            ${__('Play again', 'mavis')}
          </button>
        </div>
        <div class="wp-block-button">
          <button class="wp-block-button__link wp-element-button has-mavis-color-personality-secondary-background-color share-with-friends">
            ${__('Share with friends', 'mavis')}
          </button>
        </div>
    </div>
    ${quizAlert}`;

		results.insertAdjacentHTML('beforeend', output);
		results.classList.add('is-active');

		triggerNextQuizPopup(personality, '.mavisPersonality-results');

		document
			.querySelector('button.wp-block-button__link.share-with-friends')
			.addEventListener('click', (event) => {
				copyURLToClipboard(event);
				if (window.dataLayer) {
					window.dataLayer.push({
						event: 'personalityQuizInteraction',
						interactionType: 'share',
						quizName: document.querySelector('.wp-block-post-title').innerText,
					});
				}
			});

		document
			.querySelector('button.wp-block-button__link.play-again')
			.addEventListener('click', handlePlayAgain);
	}
};

/**
 * Checks if the amount of selected items, if all are selected remove class from button
 *
 * @param {array} personality - Array of personality quizzes on a page/post
 *
 */
const submitButtonValidation = (personality, questionId) => {
	if (allQuestionsAnswered(personality, questionId)) {
		const validationWarning = personality.querySelector('.mavisPersonality-validation-warning');

		if (validationWarning) {
			validationWarning.classList.remove('is-active');
			validationWarning.innerHTML = '';
		}

		const submitButton = personality.querySelector(
			'.mavisPersonality-submit-area .wp-block-button button',
		);
		if (submitButton.classList.contains('missing-answers')) {
			submitButton.classList.remove('missing-answers');
		}
	}
};

/**
 * Handles submit click, validation warnings and sends data to handleSubmit
 *
 * @param {element} personality - the relevant personality quiz element
 * @param {object} event - event object from click event listener
 *
 */
const handleClick = (personality, event) => {
	const personalityId = event.target.closest('.wp-block-mavis-personality').dataset.questionId;
	const questions = personality.querySelectorAll('.wp-block-mavis-personality-question');
	const questionIds = [];
	const questionNumbers = [];

	const selectedOptions = [...personality.getElementsByTagName('input')]
		.filter((input) => input.checked)
		.map((input) => input.id);

	if (selectedOptions.length < questions.length) {
		questions.forEach((question) => {
			if (question.dataset.questionAnswered === '0') {
				question.classList.add('is-unanswered');
				questionNumbers.push(question.dataset.questionNumber);
			}
		});

		const validationWarning = personality.querySelector('.mavisPersonality-validation-warning');
		const questionTotal = questionNumbers.length;
		const finalNumber = questionNumbers.pop();
		const numberOutput = questionNumbers.length
			? `${questionNumbers.join(', ')} & ${finalNumber}`
			: finalNumber;

		validationWarning.innerHTML = _nx(
			`You forgot to fill in Question ${numberOutput}`,
			`You forgot to fill in Questions ${numberOutput}`,
			questionTotal,
			'number of questions',
			I18N_DOMAIN,
		);
		validationWarning.classList.add('is-active');
		event.target.classList.add('missing-answers');
		return;
	}

	const submitArea = personality.querySelector('.mavisPersonality-submit-area');
	if (submitArea) {
		submitArea.classList.add('is-inactive');
	}

	const validationWarning = personality.querySelector('.mavisPersonality-validation-warning');
	if (validationWarning) {
		validationWarning.classList.remove('is-active');
		validationWarning.innerHTML = '';
	}

	const unselectedOptions = [...personality.getElementsByTagName('input')].filter(
		(input) => input.checked === false,
	);
	unselectedOptions.forEach((input) => {
		input.setAttribute('disabled', true);
	});

	selectedOptions.forEach((option) => {
		questionIds.push(
			document.getElementById(option).closest('.wp-block-mavis-personality-question').dataset
				.questionId,
		);
	});

	handleSubmit(selectedOptions, personalityId, questionIds);
};

/**
 * Handles input click, and highlights the chosen options
 *
 * @param {object} event - event object from click event listener
 *
 */
const handleInput = async (event) => {
	const relevantQuestion = event.target.closest('.wp-block-mavis-personality-question');
	const answer = event.target.closest('.wp-block-mavis-personality-answer').querySelector('input');

	if (window.dataLayer) {
		window.dataLayer.push({
			event: `personalityQuizInteraction`,
			interactionType: `answerSelect`,
			quizName: document.querySelector('.wp-block-post-title').innerText,
			questionNumber: relevantQuestion.getAttribute('data-question-number'),
			questionText: relevantQuestion.querySelector('h2').innerText,
			answerChosenText: answer.getAttribute('title'),
			answerChosenLetter: answer.getAttribute('data-answer-letter'),
		});
	}

	relevantQuestion.setAttribute('data-question-answered', '1');

	if (relevantQuestion.classList.contains('is-unanswered')) {
		relevantQuestion.classList.remove('is-unanswered');
	}

	const previouslySelected = relevantQuestion.querySelectorAll(
		'.wp-block-mavis-personality-answer.is-selected',
	);
	if (previouslySelected) {
		previouslySelected.forEach((element) => {
			element.classList.remove('is-selected');
		});
	}

	let wrapper = relevantQuestion.querySelector('.mavisPersonality-postAnswerContent');

	if (!wrapper) {
		wrapper = document.createElement('div');
		wrapper.className = 'mavisPersonality-postAnswerContent is-hidden';
		wrapper.setAttribute('aria-live', 'polite');
		relevantQuestion.appendChild(wrapper);
	}

	// Get the factoid element for the relevant question
	let factoidEl = wrapper.querySelector('.mavisPersonality-factoid');
	if (!factoidEl) {
		factoidEl = document.createElement('p');
		factoidEl.className = 'mavisPersonality-factoid';
		wrapper.appendChild(factoidEl);
	}

	if (!factoidEl.innerHTML.trim()) {
		const { questionId: personalityId } = event.target.closest(
			'.wp-block-mavis-personality',
		).dataset;
		const { questionId } = relevantQuestion.dataset;

		try {
			const factoidResp = await getQuestionFactoid(personalityId, questionId);
			const { factoid } = factoidResp;
			if (factoid) {
				factoidEl.innerHTML = factoid;
			}
		} catch (error) {
			console.error('Error fetching factoid:', error);
		}
	}

	// When an option has been selected, add a class to reveal the factoid
	wrapper.classList.remove('is-hidden');

	event.target.parentElement.classList.add('is-selected');

	const allAnswerEls = relevantQuestion.querySelectorAll('.wp-block-mavis-personality-answer');
	allAnswerEls.forEach((answerEl) => {
		const input = answerEl.querySelector('input');
		input.checked = answerEl.classList.contains('is-selected') || false;
	});
};

window.addEventListener('DOMContentLoaded', () => {
	const isReduced =
		window.matchMedia(`(prefers-reduced-motion: reduce)`) === true ||
		window.matchMedia(`(prefers-reduced-motion: reduce)`).matches === true;

	const personalityBlocks = document.querySelectorAll('.wp-block-mavis-personality');

	if (!personalityBlocks) {
		return;
	}

	personalityBlocks.forEach((personality) => {
		const inputGroups = personality.querySelectorAll('fieldset');
		radioGroupArrowKeyNavigation(inputGroups);

		personality.addEventListener('click', (event) => {
			if (
				event.target.tagName === 'INPUT' &&
				event.target.parentElement.classList.contains('wp-block-mavis-personality-answer')
			) {
				const individualQuestion = event.target.closest('.wp-block-mavis-personality-question');

				handleInput(event);
				submitButtonValidation(personality, individualQuestion);

				const answerId = event.target.id;
				const questionAnswered = individualQuestion.querySelectorAll(
					'.wp-block-mavis-personality-answer input',
				);

				questionAnswered.forEach((element) => {
					if (element.id !== answerId) {
						element.classList.add('mavisPersonality-question-answered');
					} else {
						element.classList.remove('mavisPersonality-question-answered');
					}
				});

				if (!isReduced) {
					jumpToFactoid(individualQuestion);
				}

				if (!allQuestionsAnswered(personality)) {
					validationCheck(personality, event, 'INPUT', 'personality');
				}
			}

			if (
				event.target.tagName === 'BUTTON' &&
				event.target.parentElement.parentElement.classList.contains('mavisPersonality-submit-area')
			) {
				event.preventDefault();
				handleClick(personality, event);
				submitButtonValidation(personality);
			}
		});
	});
});
